'use client';

import {
	ActionBar as ChakraActionBar,
	Flex,
	Text,
} from '@chakra-ui/react';
import type { ActionBarRootProps } from '@chakra-ui/react';
import * as React from 'react';

import {
	ActionBarRoot as BaseActionBarRoot,
	ActionBarContent as BaseActionBarContent,
	ActionBarSelectionTrigger as BaseActionBarSelectionTrigger,
	ActionBarCloseTrigger as BaseActionBarCloseTrigger,
} from '../../base/action-bar';

type ActionBarProps = {
	count: number
	unit?: string
} & ActionBarRootProps;

function ActionBarWithSelectFn({
	children, count, open, unit = 'selected', ...props
}: ActionBarProps) {
	const isOpen = open ?? (count > 0);

	return (
		<BaseActionBarRoot open={isOpen} {...props}>
			<ChakraActionBar.Positioner
				bottom="0"
				display="flex"
				insetInline="0"
				justifyContent="stretch"
				position="sticky"
				w="full"
			>
				<BaseActionBarContent
					bg="blackAlpha.800"
					borderRadius="md"
					display="flex"
					flex="1"
					justifyContent="space-between"
					maxW="unset"
					px="sm"
					py="xs"
					w="full"
				>
					<Flex alignItems="center" gap="sm">
						<BaseActionBarSelectionTrigger asChild>
							<Flex alignItems="center" as="button" gap="xs">
								<Text color="white" fontSize="sm" fontWeight="semibold">
									{count}
									<Text
										as="span"
										fontWeight="normal"
									>
											&nbsp;
										{unit}
									</Text>
								</Text>
							</Flex>
						</BaseActionBarSelectionTrigger>
					</Flex>

					<Flex alignItems="center" gap="sm">
						{children}
						<BaseActionBarCloseTrigger />
					</Flex>
				</BaseActionBarContent>
			</ChakraActionBar.Positioner>
		</BaseActionBarRoot>
	);
}

type Compound = ((props: ActionBarProps) => null | React.ReactElement) & {
	CloseTrigger:     React.ComponentType<unknown>
	Content:          React.ComponentType<unknown>
	Positioner:       React.ComponentType<unknown>
	Root:             React.ComponentType<unknown>
	SelectionTrigger: React.ComponentType<unknown>
	Separator:        React.ComponentType<unknown>
};

const ActionBarWithSelect = ActionBarWithSelectFn as unknown as Compound;
ActionBarWithSelect.Root = BaseActionBarRoot as unknown as React.ComponentType<unknown>;
ActionBarWithSelect.Positioner = ChakraActionBar.Positioner as unknown as React.ComponentType<unknown>;
ActionBarWithSelect.Content = BaseActionBarContent as unknown as React.ComponentType<unknown>;
ActionBarWithSelect.SelectionTrigger = BaseActionBarSelectionTrigger as unknown as React.ComponentType<unknown>;
ActionBarWithSelect.CloseTrigger = BaseActionBarCloseTrigger as unknown as React.ComponentType<unknown>;
ActionBarWithSelect.Separator = ChakraActionBar.Separator as unknown as React.ComponentType<unknown>;

export { ActionBarWithSelect };
export default ActionBarWithSelect;
